import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const { FiSettings, FiEdit, FiSliders, FiBarChart2, FiFileText, FiDownload, FiLink, FiEye, FiHeadphones, FiArrowRight, FiUsers, FiImage, FiTag, FiFolder } = FiIcons;

const ProductShowcase = () => {
  const features = [
    {
      title: "Distraction-Free Editor",
      description: "A clean, modern editor that keeps you focused on the writing - chapter after chapter, scene after scene",
      icon: FiEdit,
      position: "left"
    },
    {
      title: "Author Voice DNA Controls",
      description: "Dial in your style - match an existing book, adjust tone, and keep every chapter sounding like you",
      icon: FiSliders,
      position: "right"
    },
    {
      title: "Live KDP Preflight",
      description: "Built-in checks confirm your manuscript meets KDP's formatting and metadata requirements as you write",
      icon: FiBarChart2,
      position: "left"
    },
    {
      title: "Chapter & Scene Management",
      description: "Auto-detect chapters or organize scenes manually with simple drag-and-drop outlining",
      icon: FiFileText,
      position: "right"
    },
    {
      title: "Share Drafts",
      description: "Generate shareable links so beta readers and editors can review your draft and leave comments",
      icon: FiLink,
      position: "left"
    },
    {
      title: "One-Click Export",
      description: "Export EPUB, PDF, DOCX, and KDP-ready files - all formatted to spec, ready to publish",
      icon: FiDownload,
      position: "right"
    },
    {
      title: "Audiobook Studio",
      description: "Manuscript to finished audiobook in hours, not weeks - no audio gear or editing experience needed. Pro-quality AI narration mastered to ACX spec, exported for Amazon Virtual Voice (via KDP) and sanctioned wide distribution",
      icon: FiHeadphones,
      position: "left",
      to: "/audiobook",
      linkLabel: "Explore the Audiobook Studio"
    },
    {
      title: "Author Persona",
      description: "A persistent voice profile that learns and improves across every book you write",
      icon: FiUsers,
      position: "right",
      to: "/author-persona",
      linkLabel: "Explore Author Persona"
    },
    {
      title: "Manuscript Analysis",
      description: "Themes, craft, pacing, and real comparable-title research - see your book, truly understood",
      icon: FiBarChart2,
      position: "left",
      to: "/analysis",
      linkLabel: "Explore Manuscript Analysis"
    },
    {
      title: "Cover Studio",
      description: "Five AI cover directions, edit in plain language, export KDP-ready for every format",
      icon: FiImage,
      position: "right",
      to: "/cover-studio",
      linkLabel: "Explore Cover Studio"
    },
    {
      title: "KDP Keywords & Categories",
      description: "Optimized keywords and BISAC categories so the right readers find your book",
      icon: FiTag,
      position: "left",
      to: "/analysis",
      linkLabel: "See how it works"
    },
    {
      title: "Research & Notes",
      description: "Keep worldbuilding, characters, and references organized alongside your manuscript",
      icon: FiFolder,
      position: "right",
      to: "/analysis",
      linkLabel: "See how it works"
    }
  ];

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="product-showcase" className="py-20 bg-gradient-to-br from-parchment-100 to-secondary-50 overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        {/* Background Elements */}
        <div className="absolute top-0 left-0 w-64 h-64 bg-gradient-to-r from-primary-300/20 to-secondary-300/20 rounded-full filter blur-3xl opacity-50 transform -translate-x-1/2 -translate-y-1/2"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-gradient-to-r from-secondary-300/20 to-primary-300/20 rounded-full filter blur-3xl opacity-50 transform translate-x-1/2 translate-y-1/2"></div>

        <div className="text-center mb-16 relative z-10">
          <motion.h2
            className="text-4xl md:text-5xl font-bold text-gray-900 mb-6"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            See the{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary-500 to-secondary-500">
              Studio
            </span>{' '}
            in Action
          </motion.h2>
          <motion.p
            className="text-xl text-gray-600 max-w-3xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            One workspace for chapters, voice, preflight, sharing, and export - here's what's inside.
          </motion.p>
        </div>

        {/* Enhanced Product Screenshot with UI/UX Panel */}
        <motion.div
          className="relative mx-auto mb-20 max-w-6xl"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.3 }}
        >
          <div className="bg-gradient-to-r from-gray-800 to-gray-900 rounded-2xl shadow-2xl overflow-hidden p-3">
            <div className="flex items-center border-b border-gray-700 pb-2 px-2">
              <div className="flex space-x-1.5">
                <div className="w-3 h-3 rounded-full bg-red-500"></div>
                <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                <div className="w-3 h-3 rounded-full bg-green-500"></div>
              </div>
              <div className="mx-auto text-sm text-gray-400">ManuscriptSmith Studio</div>
            </div>

            {/* Enhanced UI Panel */}
            <div className="bg-gray-100 rounded-lg overflow-hidden">
              <div className="flex flex-col lg:flex-row h-auto lg:h-[500px]">
                {/* Left Sidebar */}
                <div className="w-full lg:w-64 bg-white border-r border-gray-200 p-4">
                  <div className="space-y-4">
                    <div className="text-sm font-semibold text-gray-700 mb-3">My Projects</div>
                    <div className="space-y-2">
                      <div className="p-3 bg-primary-50 rounded-lg border border-primary-200">
                        <div className="font-medium text-sm">The Great Novel</div>
                        <div className="text-xs text-gray-500">Chapter 12 of 24</div>
                        <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                          <div className="bg-primary-500 h-2 rounded-full" style={{ width: '50%' }}></div>
                        </div>
                      </div>
                      <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                        <div className="font-medium text-sm">Marketing Guide</div>
                        <div className="text-xs text-gray-500">Ready to export</div>
                        <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                          <div className="bg-green-500 h-2 rounded-full" style={{ width: '100%' }}></div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Main Content Area */}
                <div className="flex-1 p-4 lg:p-6">
                  <div className="mb-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Chapter Insights</h3>
                    <div className="bg-white rounded-lg p-4 border border-gray-200">
                      <div className="grid grid-cols-3 gap-4 mb-4">
                        <div className="text-center">
                          <div className="text-xl lg:text-2xl font-bold text-primary-600">2,847</div>
                          <div className="text-xs text-gray-500">Words</div>
                        </div>
                        <div className="text-center">
                          <div className="text-xl lg:text-2xl font-bold text-green-600">94%</div>
                          <div className="text-xs text-gray-500">Voice Match</div>
                        </div>
                        <div className="text-center">
                          <div className="text-xl lg:text-2xl font-bold text-blue-600">Pass</div>
                          <div className="text-xs text-gray-500">KDP Preflight</div>
                        </div>
                      </div>
                      <div className="text-sm text-gray-600">
                        <p className="mb-2"><strong>Current Chapter:</strong> "The Discovery"</p>
                        <p className="mb-2"><strong>Voice DNA:</strong> Your style - 94% match</p>
                        <p><strong>Status:</strong> Draft - 78% to target word count</p>
                      </div>
                    </div>
                  </div>

                  <div className="mb-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Chapter Management</h3>
                    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                      <div className="divide-y divide-gray-200">
                        <div className="p-3 flex items-center justify-between hover:bg-gray-50">
                          <div className="flex items-center space-x-3">
                            <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                              <SafeIcon icon={FiEye} className="w-4 h-4 text-green-600" />
                            </div>
                            <div>
                              <div className="font-medium text-sm">Chapter 1: The Beginning</div>
                              <div className="text-xs text-gray-500">2,145 words • in your voice</div>
                            </div>
                          </div>
                          <button className="text-xs bg-primary-100 text-primary-700 px-2 py-1 rounded">
                            Preview
                          </button>
                        </div>
                        <div className="p-3 flex items-center justify-between hover:bg-gray-50">
                          <div className="flex items-center space-x-3">
                            <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                              <SafeIcon icon={FiSettings} className="w-4 h-4 text-blue-600" />
                            </div>
                            <div>
                              <div className="font-medium text-sm">Chapter 2: The Journey</div>
                              <div className="text-xs text-gray-500">Drafting... 45%</div>
                            </div>
                          </div>
                          <button className="text-xs bg-gray-100 text-gray-500 px-2 py-1 rounded">
                            Edit
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Share Draft</h3>
                    <div className="bg-white rounded-lg p-4 border border-gray-200">
                      <div className="flex flex-col lg:flex-row lg:items-center justify-between mb-3">
                        <div>
                          <div className="font-medium text-sm">Share with readers</div>
                          <div className="text-xs text-gray-500">Let beta readers and editors review your draft</div>
                        </div>
                        <button className="mt-2 lg:mt-0 text-xs bg-green-100 text-green-700 px-3 py-1 rounded-full">
                          <SafeIcon icon={FiLink} className="w-3 h-3 inline mr-1" />
                          Copy Link
                        </button>
                      </div>
                      <div className="text-xs text-gray-400 font-mono bg-gray-50 p-2 rounded overflow-hidden">
                        share link · draft/abc123...
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Capabilities Grid — the concrete tools inside the studio, right below the mockup */}
        <div className="relative z-10">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-primary-700 bg-white px-3 py-1 rounded-full mb-4 shadow-sm">
              Studio Capabilities
            </div>
            <h3 className="text-3xl font-bold text-gray-900">Everything an author needs, in one place</h3>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                className="flex flex-col h-full p-6 rounded-2xl bg-white border border-gray-100 shadow-sm hover:shadow-lg transition-all duration-300"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: (index % 3) * 0.08 }}
                whileHover={{ y: -4 }}
              >
                <div className="w-12 h-12 bg-gradient-to-r from-primary-500 to-secondary-500 rounded-xl flex items-center justify-center shadow-md mb-4">
                  <SafeIcon icon={feature.icon} className="w-6 h-6 text-white" />
                </div>
                <h4 className="text-lg font-bold text-gray-900 mb-2">{feature.title}</h4>
                <p className="text-gray-600 text-sm leading-relaxed flex-1">{feature.description}</p>
                {feature.to && (
                  <Link to={feature.to} className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-primary-600 hover:text-primary-700">
                    {feature.linkLabel || 'Learn more'} <SafeIcon icon={FiArrowRight} className="w-4 h-4" />
                  </Link>
                )}
              </motion.div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <motion.div
          className="mt-16 text-center relative z-10"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.6 }}
        >
          <button
            onClick={() => scrollToSection('transform-form')}
            className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-primary-500 to-secondary-500 text-white font-bold text-lg rounded-lg hover:from-primary-600 hover:to-secondary-600 transition-all transform hover:scale-105 shadow-xl"
          >
            Get started
          </button>
          <p className="mt-4 text-gray-600">See plans and pricing</p>
        </motion.div>
      </div>
    </section>
  );
};

export default ProductShowcase;